/**
 * ZugriffFreigabe — W3 Managed-Access surface on the Kunde detail page (PART C5
 * + Email sharing v2, 2026-08-10).
 *
 * "Share a client's communications." A user with full access to the subject
 * grants access (POST /communications/access-grants), sees the standing grants,
 * and can revoke one (DELETE …/access-grants/:id).
 *
 * Email sharing v2 expands the form to pick:
 *  - TARGET: a colleague (user select) OR a group (funktionsgruppe select).
 *  - SCOPE:  this email/thread (when a comm is in context) | this client (subject) |
 *            this client domain (all mail from a sender domain) | whole workspace.
 *  - LEVEL:  Vollzugriff (full) | Nur Betreff (title). Default = Nur Betreff. The
 *            options are capped at the sharer's own tier (`sharerTier`, default full);
 *            the API is the authority and rejects above-tier grants with 403.
 *  - ABLAUF: dauerhaft | 7 Tage | 30 Tage.
 *
 * Groups reuse the existing Funktionsgruppen; a "Gruppen verwalten" escape hatch
 * links to Administration › Teams (create a group + add members). No new group
 * backend — the funktionsgruppe endpoints already exist.
 *
 * Live-only: in mock mode the surface renders a hint (no backend). The colleague
 * + group pickers read GET /admin/members + /admin/funktionsgruppen; where those
 * are unavailable (403) the pickers degrade to an empty state.
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useFreigabe.ts]
 * [Source: apps/api/src/modules/communication/dto/comm-managed-access.dto.ts]
 * [Source: docs/design/2026-08-10_email-sharing-model-groups-domain-tier.md §5]
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Trash2, UserPlus, Users, Settings2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { apiClient } from '../../../lib/api-client';
import type { ManagedAccessGrantItem } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import {
  useAccessGrants,
  useGrantManagedAccess,
  useRevokeGrant,
} from '../../communication-v2/hooks/useFreigabe';

type TargetMode = 'user' | 'group';
type ScopeMode = 'comm' | 'subject' | 'domain' | 'workspace';
type Tier = 'full' | 'title';
type Ablauf = 'permanent' | '7' | '30';

interface Props {
  subjectType: 'person' | 'firma';
  subjectId: string;
  subjectName: string;
  /**
   * Optional: when the surface is opened in an email context, the comm in view.
   * Enables the "Diese E-Mail / Thread" scope option. Absent on the Kunde page.
   */
  commId?: string;
  /** Optional prefill for the client's sender domain (the domain-scope input). */
  defaultDomain?: string;
  /**
   * The sharer's own effective tier on the subject — caps the grantable level
   * (a title-only sharer cannot grant Vollzugriff). Defaults to 'full' since the
   * surface is intended for full-access sharers; the API is the final authority.
   */
  sharerTier?: Tier;
  onFeedback?: (msg: string) => void;
  onError?: (msg: string) => void;
}

const TIER_LABEL: Record<string, string> = {
  full: 'Vollzugriff',
  title: 'Nur Betreff',
  metadata: 'Metadaten',
};

const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

function scopeLabel(g: ManagedAccessGrantItem): string {
  if (g.comm_id) return 'Diese E-Mail / Thread';
  if (g.domain) return `Domain: ${g.domain}`;
  if (g.subject_id) return 'Dieser Kunde';
  return 'Gesamter Arbeitsbereich';
}

function ablaufToExpiresAt(ablauf: Ablauf): string | undefined {
  if (ablauf === 'permanent') return undefined;
  const days = ablauf === '7' ? 7 : 30;
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

export function ZugriffFreigabe({
  subjectType,
  subjectId,
  subjectName,
  commId,
  defaultDomain = '',
  sharerTier = 'full',
  onFeedback,
  onError,
}: Props) {
  const live = isLiveData();
  const [targetMode, setTargetMode] = useState<TargetMode>('user');
  const [userId, setUserId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [scope, setScope] = useState<ScopeMode>('subject');
  const [domain, setDomain] = useState(defaultDomain);
  const [tier, setTier] = useState<Tier>('title');
  const [ablauf, setAblauf] = useState<Ablauf>('permanent');

  // Colleague picker (GET /admin/members; role.read). Degrades to empty on 403.
  const { data: membersData } = useQuery({
    queryKey: qk.adminMembers(),
    queryFn: () => apiClient.admin.members(),
    enabled: live,
    retry: false,
  });
  const members = membersData?.members ?? [];
  const memberName = (id: string | null): string => {
    if (!id) return 'Unbekannt';
    const m = members.find((x) => x.user_id === id);
    return m ? (m.display_name ?? m.email) : id.slice(0, 8) + '…';
  };

  // Group picker (GET /admin/funktionsgruppen; funktionsgruppe.read). Degrades to empty on 403.
  const { data: groupsData } = useQuery({
    queryKey: qk.adminFunktionsgruppen(),
    queryFn: () => apiClient.admin.funktionsgruppen(),
    enabled: live,
    retry: false,
  });
  const groups = groupsData?.funktionsgruppen ?? [];

  const { data: grants = [] } = useAccessGrants({ subject_type: subjectType, subject_id: subjectId });
  const grant = useGrantManagedAccess((m) => onError?.(m));
  const revoke = useRevokeGrant((m) => onError?.(m));

  const tierOptions: Tier[] = sharerTier === 'full' ? ['full', 'title'] : ['title'];
  const targetChosen = targetMode === 'user' ? Boolean(userId) : Boolean(groupId);
  const domainValid = scope !== 'domain' || DOMAIN_PATTERN.test(domain.trim());
  const canSubmit = targetChosen && domainValid && !grant.isPending;

  function resetForm() {
    setUserId('');
    setGroupId('');
    setDomain(defaultDomain);
    setAblauf('permanent');
    setTier('title');
  }

  function submitGrant(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    const target =
      targetMode === 'user' ? { user_id: userId } : { funktionsgruppe_id: groupId };

    let scopeFields: Record<string, string> = {};
    if (scope === 'comm' && commId) scopeFields = { comm_id: commId };
    else if (scope === 'domain') scopeFields = { domain: domain.trim() };
    else if (scope === 'subject') scopeFields = { subject_type: subjectType, subject_id: subjectId };
    // scope === 'workspace' → no scope fields (tenant-wide).

    const expiresAt = ablaufToExpiresAt(ablauf);

    grant.mutate(
      {
        ...target,
        ...scopeFields,
        visibility_tier: tier,
        ...(expiresAt ? { expires_at: expiresAt } : {}),
      },
      {
        onSuccess: () => {
          onFeedback?.('Zugriff freigegeben');
          resetForm();
        },
      },
    );
  }

  if (!live) {
    return (
      <div className="rounded-lg border border-dashed bg-card p-6 text-center text-sm text-muted-foreground">
        Zugriffs-Freigaben sind nur mit verbundenem Backend verfügbar.
      </div>
    );
  }

  const inputCls =
    'h-9 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="flex flex-col gap-5">
      {/* Grant form */}
      <form onSubmit={submitGrant} className="flex flex-col gap-4 rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Zugriff freigeben</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Gib einer Kollegin, einem Kollegen oder einer Gruppe Zugriff auf die Kommunikation von{' '}
          <span className="font-medium text-foreground">{subjectName}</span>.
        </p>

        {/* Target: colleague vs group */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Empfänger</span>
          <div className="flex gap-2" role="radiogroup" aria-label="Empfängertyp">
            <button
              type="button"
              role="radio"
              aria-checked={targetMode === 'user'}
              onClick={() => setTargetMode('user')}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${targetMode === 'user' ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground'}`}
            >
              <UserPlus className="size-4" /> Kollege/in
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={targetMode === 'group'}
              onClick={() => setTargetMode('group')}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${targetMode === 'group' ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground'}`}
            >
              <Users className="size-4" /> Gruppe
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {/* User OR group select */}
          {targetMode === 'user' ? (
            <div className="flex min-w-48 flex-1 flex-col gap-1.5">
              <label htmlFor="grant-user" className="text-xs text-muted-foreground">
                Mitarbeiter/in
              </label>
              <select
                id="grant-user"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className={inputCls}
              >
                <option value="">Auswählen …</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name ?? m.email}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex min-w-48 flex-1 flex-col gap-1.5">
              <label htmlFor="grant-group" className="text-xs text-muted-foreground">
                Gruppe
              </label>
              <select
                id="grant-group"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className={inputCls}
              >
                <option value="">Auswählen …</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              <a
                href="/admin/teams"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Settings2 className="size-3" /> Gruppen verwalten
              </a>
            </div>
          )}

          {/* Scope */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="grant-scope" className="text-xs text-muted-foreground">
              Umfang
            </label>
            <select
              id="grant-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as ScopeMode)}
              className={inputCls}
            >
              {commId && <option value="comm">Diese E-Mail / Thread</option>}
              <option value="subject">Dieser Kunde</option>
              <option value="domain">Diese Kundendomain</option>
              <option value="workspace">Gesamter Arbeitsbereich</option>
            </select>
          </div>

          {/* Level (capped at sharer's tier) */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="grant-tier" className="text-xs text-muted-foreground">
              Umfangsstufe
            </label>
            <select
              id="grant-tier"
              value={tier}
              onChange={(e) => setTier(e.target.value as Tier)}
              className={inputCls}
            >
              {tierOptions.map((t) => (
                <option key={t} value={t}>
                  {TIER_LABEL[t]}
                </option>
              ))}
            </select>
          </div>

          {/* Ablauf */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="grant-ablauf" className="text-xs text-muted-foreground">
              Ablauf
            </label>
            <select
              id="grant-ablauf"
              value={ablauf}
              onChange={(e) => setAblauf(e.target.value as Ablauf)}
              className={inputCls}
            >
              <option value="permanent">Dauerhaft</option>
              <option value="7">7 Tage</option>
              <option value="30">30 Tage</option>
            </select>
          </div>

          <Button type="submit" size="sm" disabled={!canSubmit}>
            <UserPlus className="size-4" />
            Freigeben
          </Button>
        </div>

        {/* Domain input (only for the domain scope) */}
        {scope === 'domain' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="grant-domain" className="text-xs text-muted-foreground">
              Kundendomain (z. B. kunde.ch): jede eingehende Mail von dieser Domain
            </label>
            <input
              id="grant-domain"
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="kunde.ch"
              aria-invalid={!domainValid}
              className={`${inputCls} max-w-xs ${!domainValid ? 'border-status-danger' : ''}`}
            />
            {!domainValid && domain.trim() !== '' && (
              <span className="text-xs text-status-danger">Bitte eine gültige Domain angeben.</span>
            )}
          </div>
        )}

        {targetMode === 'user' && members.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Keine Mitarbeitenden verfügbar (erfordert die Berechtigung, Mitglieder zu sehen).
          </p>
        )}
        {targetMode === 'group' && groups.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Keine Gruppen vorhanden. Lege unter „Gruppen verwalten“ eine Gruppe an.
          </p>
        )}
      </form>

      {/* Standing grants */}
      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="border-b border-border px-4 py-2.5">
          <h3 className="text-sm font-semibold text-foreground">Bestehende Freigaben</h3>
        </div>
        {grants.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Keine Freigaben für {subjectName}.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {grants.map((g) => {
              const targetName = g.funktionsgruppe_id
                ? `${g.funktionsgruppe_name ?? 'Gruppe'} (${g.funktionsgruppe_member_count ?? 0})`
                : memberName(g.user_id);
              return (
                <li key={g.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate font-medium text-foreground">
                      {g.funktionsgruppe_id && <Users className="size-3.5 text-muted-foreground" />}
                      {targetName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {scopeLabel(g)}
                      {' · '}
                      {g.expires_at
                        ? `Läuft ab am ${new Date(g.expires_at).toLocaleDateString('de-CH')}`
                        : 'Unbefristet'}
                    </p>
                  </div>
                  <Badge variant="neutral">{TIER_LABEL[g.visibility_tier] ?? g.visibility_tier}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      revoke.mutate(g.id, { onSuccess: () => onFeedback?.('Freigabe widerrufen') })
                    }
                    disabled={revoke.isPending}
                    aria-label={`Freigabe für ${targetName} widerrufen`}
                  >
                    <Trash2 className="size-4" />
                    Widerrufen
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
